import { If, VLazyLoadMultiSelect, VSelect, VSelectOptionWithDescription } from '@vision/ui/components';

import { QuestionStyle } from '@vision/ui/enums';
import { useEmojiContext, useSelectedAccountId } from '@vision/ui/hooks';
import { ReportsDetailGraphicSchemaValues, SimpleFlowQuestion } from '@vision/ui/interfaces';
import {
  useLazyFetchFlowQuestionsWithPaginationQuery,
  useLazyFetchFlowsWithPaginationQuery,
} from '@vision/ui/services';
import { getBodyText, isObjectNotEmpty, translateErrorMessage } from '@vision/ui/utils';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { ReportsDetailGraphicCompareLayout } from '../ReportsDetailGraphicCompareLayout';
import { ReportsDetailGraphicBaseProps } from '../report-detail-graphic-compare.util';

const RESPONSE_QUESTION_TYPES = [QuestionStyle.STAR, QuestionStyle.PLAIN, QuestionStyle.SCORE];

interface ReportsDetailGraphicCompareQuestionScoreProps extends ReportsDetailGraphicBaseProps {}

export function ReportsDetailGraphicCompareQuestionScore({
  compareItemType,
  required,
}: ReportsDetailGraphicCompareQuestionScoreProps) {
  const { t, i18n } = useTranslation();
  const accountId = useSelectedAccountId();
  const formik = useFormikContext<ReportsDetailGraphicSchemaValues>();
  const { textWithNativeEmoji } = useEmojiContext();
  const selectedQuestionStyle = formik.values.selectedCompareCriteria.selectedQuestionOptionScoreStyle;
  const selectedFlowIds = formik.values.selectedCompareCriteria.selectedQuestionOptionScoreFlowIds || [];
  const isFlowSelected = selectedFlowIds.length > 0;

  const questionStyleOptions = useMemo(
    () =>
      RESPONSE_QUESTION_TYPES.map((style) => ({
        value: style,
        label: t(`translation:questionStyles.${style}`),
      })),
    [t, i18n.language],
  );

  const setQuestionStyle = (value: QuestionStyle | null) => {
    formik.setValues({
      ...formik.values,
      selectedCompareCriteria: {
        ...formik.values.selectedCompareCriteria,
        selectedQuestionOptionScoreStyle: value,
        selectedQuestionOptionScoreFlowIds: formik.values.selectedCompareCriteria.selectedQuestionOptionScoreFlowIds,
        selectedQuestionOptionScoreQuestionIds: [],
      },
    });
  };

  const handleChangeQuestionStyle = (value: QuestionStyle | null) => {
    if (value === selectedQuestionStyle) {
      return;
    }

    setQuestionStyle(value);
  };

  const handleDefaultQuestionValuesLoaded = (data: SimpleFlowQuestion[]) => {
    const selectedQuestionIds = formik.values.selectedCompareCriteria.selectedQuestionOptionScoreQuestionIds;

    if (!selectedQuestionIds.length) {
      return;
    }

    const validSelectedQuestionIds = selectedQuestionIds.filter((id) => data.some((item) => item.id === id));

    if (validSelectedQuestionIds.length !== selectedQuestionIds.length) {
      formik.setFieldValue('selectedCompareCriteria.selectedQuestionOptionScoreQuestionIds', validSelectedQuestionIds);
    }

    if (selectedQuestionStyle) {
      return;
    }

    const matchedQuestionStyle = data.find((item) => validSelectedQuestionIds.includes(item.id) && item.style)?.style;

    if (matchedQuestionStyle && RESPONSE_QUESTION_TYPES.includes(matchedQuestionStyle as QuestionStyle)) {
      formik.setFieldValue(
        'selectedCompareCriteria.selectedQuestionOptionScoreStyle',
        matchedQuestionStyle as QuestionStyle,
      );
    }
  };

  const handleChangeFlow = (value: string[]) => {
    formik.setValues({
      ...formik.values,
      selectedCompareCriteria: {
        ...formik.values.selectedCompareCriteria,
        selectedQuestionOptionScoreFlowIds: value,
        selectedQuestionOptionScoreQuestionIds: [],
      },
    });
  };

  return (
    <ReportsDetailGraphicCompareLayout title={t(`page-reports:reportCompareGroupItemType.${compareItemType}`)}>
      <VLazyLoadMultiSelect
        id="report-compare-question-key-flow"
        data-testid="input-report-compare-question-key-flow"
        label={t('flows')}
        useLazyApiQueryFunction={useLazyFetchFlowsWithPaginationQuery}
        apiRequestParameters={{
          nodeId: accountId,
          sortBy: 'name',
          sortOrder: 'asc',
          questionStyles: RESPONSE_QUESTION_TYPES,
        }}
        multiSelectItemMapper={(items) => {
          return items.map((item) => ({
            value: item.id,
            label: item.name,
          }));
        }}
        searchKey="flow_name"
        value={selectedFlowIds}
        searchable={true}
        onBlur={formik.handleBlur}
        onChange={handleChangeFlow}
        error={translateErrorMessage(formik, 'selectedCompareCriteria.selectedQuestionOptionScoreFlowIds')}
        withAsterisk={required}
        itemComponent={VSelectOptionWithDescription}
      />

      <If value={isFlowSelected}>
        <VSelect
          id="report-compare-question-option-score-type"
          data-testid="input-report-compare-question-option-score-type"
          label={t('translation:questionType')}
          data={questionStyleOptions}
          value={selectedQuestionStyle}
          withAsterisk={required}
          searchable={false}
          clearable={true}
          onChange={(value) => handleChangeQuestionStyle(value as QuestionStyle | null)}
          error={translateErrorMessage(formik, 'selectedCompareCriteria.selectedQuestionOptionScoreStyle')}
        />

        <VLazyLoadMultiSelect
          id="report-compare-flow-question-option-score"
          data-testid="input-report-compare-flow-question-option-score"
          label={t('question')}
          useLazyApiQueryFunction={useLazyFetchFlowQuestionsWithPaginationQuery}
          apiRequestParameters={{
            nodeId: accountId,
            flowIds: selectedFlowIds,
            sortBy: 'name',
            sortOrder: 'asc',
            style: selectedQuestionStyle ? [selectedQuestionStyle] : [],
          }}
          multiSelectItemMapper={(items) => {
            return items
              .filter((item) => isObjectNotEmpty(item.body))
              .map((item) => ({
                value: item.id,
                label: textWithNativeEmoji(getBodyText(item.body, i18n.language)),
                description: item?.flow_name,
              }));
          }}
          searchKey="key"
          searchable={true}
          value={formik.values.selectedCompareCriteria.selectedQuestionOptionScoreQuestionIds}
          onBlur={formik.handleBlur}
          onChange={(value) =>
            formik.setFieldValue('selectedCompareCriteria.selectedQuestionOptionScoreQuestionIds', value)
          }
          error={translateErrorMessage(formik, 'selectedCompareCriteria.selectedQuestionOptionScoreQuestionIds')}
          resetChangeRequestParameters={true}
          itemComponent={VSelectOptionWithDescription}
          withAsterisk={required}
          disabled={!selectedQuestionStyle}
          onDefaultValuesLoaded={handleDefaultQuestionValuesLoaded}
        />
      </If>
    </ReportsDetailGraphicCompareLayout>
  );
}
